import api from '../../../services/api';
import { NormalizeScanCode } from './collectorScan';

/**
 * Núcleo NÃO-VISUAL das Ordens de Serviço no coletor (M5).
 *
 * É o irmão do `collectorScan.js` do M4: ali mora a gramática do BIPE, aqui a
 * gramática da OS — ler o envelope da lista/ficha, achar o próximo item da
 * rota, montar as duas pontas de um item na forma que as telas do M4 já sabem
 * desenhar, e — a parte que mais importa no chão — **traduzir os 409**.
 *
 * ── Por que os 409 ganham um lugar próprio ──────────────────────────────────
 * No M5 o mesmo código HTTP significa três coisas MUITO diferentes para quem
 * está de luva em cima da empilhadeira:
 *
 *   1. **"Outro operador pegou primeiro"** (aceite first-wins) — não é falha
 *      de ninguém, é a fila funcionando. A tela recarrega e segue.
 *   2. **"Você já tem uma OS ativa"** — é uma instrução: pause a atual.
 *   3. **"Aguardando o par"** — é ESPERA, não erro. O operador chegou antes da
 *      outra metade do par; a carga ainda não está lá. Tela vermelha aqui seria
 *      mentira: ninguém errou, e a ação certa é aguardar, não "tentar de novo
 *      direito".
 *
 * Enquanto isso, o 409 do motor de movimentação (§6.1) é erro de verdade e
 * continua na tela vermelha do M4. Quem separa um do outro é este arquivo, num
 * lugar só — se essa leitura ficasse espalhada pelas telas, bastaria uma delas
 * pintar espera de vermelho para o operador aprender a ignorar a cor.
 *
 * ── Tolerância deliberada de envelope ───────────────────────────────────────
 * O backend da EXECUÇÃO do item sobe em paralelo a esta tela. O contrato do
 * corpo está fixado (`{operator_code, scan_method, lpn_code, to_location_code,
 * from_location_code?}`), mas o formato do 409 de sincronia ainda não existe em
 * código. Em vez de adivinhar UMA chave e quebrar, `ParsePairWait` reconhece a
 * espera por várias pistas e devolve o que conseguir — mesma disciplina do
 * `ParseScanResult` do M4.
 */

// ── Estados da OS, vistos do chão ───────────────────────────────────────────
export const SERVICE_ORDER_STATUS_READY = 'READY';
export const SERVICE_ORDER_STATUS_IN_PROGRESS = 'IN_PROGRESS';
export const SERVICE_ORDER_STATUS_PAUSED = 'PAUSED';
export const SERVICE_ORDER_STATUS_DONE = 'DONE';
export const SERVICE_ORDER_STATUS_CANCELED = 'CANCELED';

/** As duas em que a OS está NA MÃO de alguém (o "1 OS ativa por operador"). */
export const SERVICE_ORDER_ACTIVE_STATUSES = Object.freeze([
    SERVICE_ORDER_STATUS_IN_PROGRESS,
    SERVICE_ORDER_STATUS_PAUSED,
]);

export const SERVICE_ORDER_TERMINAL_STATUSES = Object.freeze([
    SERVICE_ORDER_STATUS_DONE,
    SERVICE_ORDER_STATUS_CANCELED,
]);

/** Item encerrado — DONE ou pulado. Pulado CONTA como resolvido (§6.5). */
export const SERVICE_ORDER_ITEM_TERMINAL_STATUSES = Object.freeze(['DONE', 'SKIPPED_ALERT']);

/** Motivos de pausa do contrato (a ordem é a da tela). */
export const PAUSE_REASONS = Object.freeze(['LUNCH', 'SHIFT_END', 'INCIDENT', 'OTHER']);

/**
 * Os "o que aconteceu" que o operador escolhe ao pular um item. É um
 * subconjunto do enum de `wms_incidents`: `WO_CANCELED_WITH_LOAD` e
 * `UNREGISTERED_OCCUPANCY` existem, mas não são coisas que se constata no meio
 * de um item de rota — oferecer opção que nunca é a certa só faz o operador
 * escolher a primeira da lista.
 */
export const SKIP_INCIDENT_KINDS = Object.freeze([
    'MISSING_PALLET',
    'OCCUPIED_DESTINATION',
    'SCAN_MISMATCH',
    'OTHER',
]);

// ── A cadeia de bipes de UM item ────────────────────────────────────────────
// Mesma gramática do M4 (origem → LPN → destino), com uma diferença que muda
// tudo: aqui os três códigos são CONHECIDOS antes de o operador sair andando,
// então todo passo tem esperado explícito.
export const ITEM_STEP_ORIGIN = 'ORIGIN';
export const ITEM_STEP_LPN = 'LPN';
/**
 * PICKING FRACIONADO: o passo em que o operador tira as unidades do pallet,
 * UMA A UMA. Não existe em item de pallet inteiro — por isso os passos são do
 * ITEM (`ServiceOrderItemSteps`), não uma constante global.
 */
export const ITEM_STEP_UNITS = 'UNITS';
/**
 * O "00" — a CABECEIRA do rack (`STG-A-00`). O operador desce o pallet da
 * posição alta, encosta no chão da cabeceira e bipa: é a prova de que o pallet
 * saiu do vão antes de ele dizer que levou à doca. Era o 00 da RUA; com o
 * porta-pallet virando Rack, é o 00 do Rack.
 *
 * Só existe para pallet INTEIRO vindo de posição alta: no fracionado o pallet
 * fica no lugar (só as unidades andam), e origem que já é chão não desce.
 */
export const ITEM_STEP_STAGING = 'STAGING';
export const ITEM_STEP_DESTINATION = 'DESTINATION';
export const ITEM_STEPS = Object.freeze([ITEM_STEP_ORIGIN, ITEM_STEP_LPN, ITEM_STEP_DESTINATION]);

/**
 * Espera entre duas unidades (ms). Serve a DUAS coisas: o leitor de código de
 * barras dispara duas leituras da mesma etiqueta com um toque longo (contaria
 * 2 por 1), e o operador precisa de um instante para SEPARAR fisicamente a
 * unidade no carrinho antes de ir na próxima.
 */
export const UNIT_SCAN_COOLDOWN_MS = 3000;

/**
 * Item FRACIONADO = tem produto e quantidade (o picker leva N unidades e o
 * pallet FICA na posição). Item sem produto/qty é o pallet inteiro do M5.
 * Quem decide isso é a GERAÇÃO da OS (≥6 un vira pallet inteiro, ≤5 fica
 * fracionado) — a tela só reflete o que o item diz.
 */
export function IsFractionalItem(item) {
    // Pallet INTEIRO com produto e quantidade não é fracionado: o operador leva
    // o pallet todo, e é a reserva quem decide isso (`whole_pallet`) — a
    // quantidade sozinha não distingue 20-de-um-pallet-de-100 (fracionado) de
    // 20-de-um-pallet-de-20 (inteiro).
    if (item?.whole_pallet === true) {
        return false;
    }
    return (
        item?.product_id !== null &&
        item?.product_id !== undefined &&
        item?.qty !== null &&
        item?.qty !== undefined &&
        Number(item.qty) > 0
    );
}

/** Quantidade a separar (fracionado); `null` em pallet inteiro. */
export function ItemUnitCount(item) {
    return IsFractionalItem(item) ? Number(item.qty) : null;
}

/** A cadeia de passos DESTE item — com unidades só quando é fracionado. */
/**
 * O pallet tem de DESCER antes de sair? Só quando ele vai inteiro e está numa
 * posição de rack com andar acima do térreo — é aí que existe o gesto de
 * baixar até a cabeceira e bipar o 00.
 */
export function NeedsStagingStep(item) {
    // Só desce quem VAI INTEIRO: no fracionado o pallet fica na posição.
    if (IsFractionalItem(item)) {
        return false;
    }
    const rack = item?.from_location_rack;
    const andar = Number(item?.from_location_level);
    return Boolean(rack) && Number.isFinite(andar) && andar > 0;
}

/** O código do 00 deste item: a cabeceira do rack de ORIGEM. */
export function StagingStepCode(item) {
    const rack = item?.from_location_rack;
    return rack ? `STG-${String(rack).toUpperCase()}-00` : null;
}

/**
 * O item tem ORIGEM? Sem ela (pallet "em formação" da fábrica, pedido de
 * reabastecimento) não há etiqueta de origem para bipar: o passo começa no LPN.
 */
export function HasItemOrigin(item) {
    return Boolean(item?.from_location_code);
}

/**
 * DESTINO LIVRE: o item nasceu sem destino e quem escolhe onde guardar é o
 * operador — bipa o bloco (ou o endereço) e só DEPOIS aponta a posição na torre.
 */
export function HasFreeDestination(item) {
    return Boolean(item) && !item.to_location_code;
}

/**
 * `ultimoItem` decide se o passo do DESTINO entra.
 *
 * No picking fracionado o picker enche o CARRINHO — item 1, item 2, item 3 — e
 * só então empurra até o packing. Pedir o bipe do destino a cada item o fazia
 * ir e voltar por item, o oposto do que a OS existe para fazer. Então o
 * destino aparece UMA VEZ, no último item: é a entrega, e o bipe prova que o
 * carrinho chegou.
 *
 * Pallet inteiro é outra história: cada pallet vai numa viagem, então cada
 * item termina no seu destino.
 *
 * Item SEM origem pula o passo da origem: não há etiqueta a bipar, e o pallet
 * é achado pelo LPN (onde quer que esteja).
 */
export function ServiceOrderItemSteps(item, { ultimoItem = true } = {}) {
    const steps = itemStepsWithOrigin(item, { ultimoItem });
    return HasItemOrigin(item) ? steps : steps.filter((step) => step !== ITEM_STEP_ORIGIN);
}

function itemStepsWithOrigin(item, { ultimoItem }) {
    if (NeedsStagingStep(item)) {
        return [ITEM_STEP_ORIGIN, ITEM_STEP_LPN, ITEM_STEP_STAGING, ITEM_STEP_DESTINATION];
    }
    if (IsFractionalItem(item)) {
        return ultimoItem
            ? [ITEM_STEP_ORIGIN, ITEM_STEP_LPN, ITEM_STEP_UNITS, ITEM_STEP_DESTINATION]
            : [ITEM_STEP_ORIGIN, ITEM_STEP_LPN, ITEM_STEP_UNITS];
    }
    return [ITEM_STEP_ORIGIN, ITEM_STEP_LPN, ITEM_STEP_DESTINATION];
}

/** Este é o último item que falta cumprir na OS? */
export function IsLastPendingItem(order, item) {
    if (!item) {
        return true;
    }
    const pendentes = ServiceOrderItems(order).filter((linha) => !IsServiceOrderItemTerminal(linha));
    return pendentes.length <= 1;
}

// ── Leitura de envelope ─────────────────────────────────────────────────────

function asObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

/** Lista da fila: `{results, paging}` (ou o array cru, por tolerância). */
export function ParseServiceOrderList(payload) {
    if (Array.isArray(payload)) {
        return payload.filter((row) => asObject(row));
    }
    const root = asObject(payload);
    const results = root && Array.isArray(root.results) ? root.results : [];
    return results.filter((row) => asObject(row));
}

/** Ficha: o DTO da OS (com `items`/`pauses`) — ou null se não veio nada útil. */
export function ParseServiceOrder(payload) {
    const root = asObject(payload);
    if (!root) {
        return null;
    }
    // Algumas rotas do projeto respondem `{service_order: {...}}`; a maioria
    // devolve o DTO direto. Ler as duas formas aqui evita que a tela dependa de
    // qual delas o backend escolheu.
    return asObject(root.service_order) || (root.id || root.code ? root : null);
}

/** Itens na ordem da rota (o backend já ordena; a ordenação aqui é defensiva). */
export function ServiceOrderItems(order) {
    const items = Array.isArray(order?.items) ? order.items.filter((item) => asObject(item)) : [];
    return [...items].sort((left, right) => Number(left.seq ?? 0) - Number(right.seq ?? 0));
}

export function IsServiceOrderItemTerminal(item) {
    return SERVICE_ORDER_ITEM_TERMINAL_STATUSES.includes(String(item?.status || '').toUpperCase());
}

/**
 * O item que o operador tem que fazer AGORA: o primeiro que ainda não está
 * encerrado. Item pulado não volta para a fila (§6.5) — por isso "não
 * terminal", e não "PENDING".
 */
export function NextServiceOrderItem(order) {
    return ServiceOrderItems(order).find((item) => !IsServiceOrderItemTerminal(item)) || null;
}

/** Contagem para a barra de progresso e para a tela de conclusão. */
export function ServiceOrderProgress(order) {
    const items = ServiceOrderItems(order);
    const done = items.filter((item) => String(item.status).toUpperCase() === 'DONE').length;
    const skipped = items.filter((item) => String(item.status).toUpperCase() === 'SKIPPED_ALERT').length;
    const current = items.findIndex((item) => !IsServiceOrderItemTerminal(item));
    return {
        total: items.length,
        done,
        skipped,
        // 1-based para falar com gente ("item 3 de 7"); no fim da rota fica no
        // total, que é o que a tela de conclusão mostra.
        position: current === -1 ? items.length : current + 1,
    };
}

/**
 * As duas pontas de um item na forma que as telas do M4 já sabem desenhar
 * (`FormatLocationPlain`, `ExpectedScanCodeFor`, `BlockTower`). O DTO manda as
 * coordenadas achatadas (`from_location_street`…) porque é uma linha de join;
 * remontar o objeto aqui é o que evita cada tela conhecer esse detalhe.
 */
export function ServiceOrderItemLocation(item, side) {
    if (!item) {
        return null;
    }
    const prefix = side === ITEM_STEP_ORIGIN ? 'from_location' : 'to_location';
    const code = item[`${prefix}_code`] || null;
    if (!code) {
        return null;
    }
    return {
        id: item[`${prefix}_id`] ?? null,
        code,
        type: item[`${prefix}_type`] ?? null,
        // DTO fala rack/bay desde o 005 (o `_block` do wire é o VÃO).
        rack: item[`${prefix}_rack`] ?? null,
        bay: item[`${prefix}_block`] ?? null,
        level: item[`${prefix}_level`] ?? null,
        position: item[`${prefix}_position`] ?? null,
    };
}

/**
 * Separa a fila do coletor em "a MINHA" e "as que dá para pegar".
 *
 * A visão restrita do backend responde `READY` + as do crachá informado, tudo
 * junto e incluindo as já encerradas. O crachá do dispositivo pode ter trocado
 * entre uma carga e outra, então a checagem de dono é refeita aqui: OS ativa de
 * OUTRO crachá não é "minha" nem aparece como disponível.
 */
export function SplitCollectorQueue(orders = [], operatorCode = null) {
    const badge = NormalizeScanCode(operatorCode);
    const ready = [];
    let active = null;

    for (const order of orders) {
        const status = String(order?.status || '').toUpperCase();
        if (SERVICE_ORDER_ACTIVE_STATUSES.includes(status)) {
            const owner = NormalizeScanCode(order?.operator_code);
            if (!active && (!badge || !owner || owner === badge)) {
                active = order;
            }
            continue;
        }
        if (status === SERVICE_ORDER_STATUS_READY) {
            ready.push(order);
        }
    }

    return { active, ready };
}

// ── Leitura dos 409 (o coração deste arquivo) ───────────────────────────────

const errorCode = (error) => String(error?.code || '').toUpperCase();
const errorDetails = (error) => asObject(error?.details) || {};
const isConflict = (error) => Number(error?.status) === 409;

/** "Outro operador aceitou primeiro" — resposta legítima, não falha. */
export function IsServiceOrderTakenError(error) {
    return isConflict(error) && errorCode(error) === 'WMS26_SERVICE_ORDER_TAKEN';
}

/**
 * "Você já está na OS X" — a trava de 1 OS ativa por operador. Devolve a OS
 * ativa para a tela poder OFERECER O CAMINHO (abrir a atual e pausá-la) em vez
 * de só dizer não.
 */
export function ParseOperatorBusy(error) {
    if (!isConflict(error) || errorCode(error) !== 'WMS26_SERVICE_ORDER_OPERATOR_BUSY') {
        return null;
    }
    const details = errorDetails(error);
    return {
        id: details.active_service_order_id ?? null,
        code: details.active_service_order_code ?? null,
        status: details.active_status ?? null,
    };
}

/** Crachá diferente do dono da OS (pausar/retomar/executar). */
export function IsOperatorMismatchError(error) {
    return isConflict(error) && errorCode(error) === 'WMS26_SERVICE_ORDER_OPERATOR_MISMATCH';
}

/** A OS/o item mudou de estado por baixo da tela — recarregar resolve. */
export function IsStaleStateError(error) {
    return (
        isConflict(error) &&
        ['WMS26_SERVICE_ORDER_TRANSITION', 'WMS26_SERVICE_ORDER_ITEM_TRANSITION'].includes(errorCode(error))
    );
}

/** Bloqueio da engine de movimentação (§6.1) — este É erro, e é vermelho. */
export function IsMovementBlockedError(error) {
    return (
        isConflict(error) &&
        (errorCode(error) === 'WMS26_MOVEMENT_BLOCKED' || Array.isArray(errorDetails(error).blocks))
    );
}

/** Bipe de origem divergente do que o backend sabe (esperado × lido). */
export function ParseOriginMismatch(error) {
    if (!isConflict(error) || errorCode(error) !== 'WMS26_MOVEMENT_ORIGIN_MISMATCH') {
        return null;
    }
    const details = errorDetails(error);
    return {
        expected: details.expected ?? details.lpn_location_code ?? null,
        read: details.read ?? details.from_location_code ?? null,
    };
}

// A sincronia do par ainda não tem código de erro escrito no backend (o motor
// da execução sobe em paralelo). Reconhecer a espera por MAIS DE UMA pista é o
// que impede o pior desfecho possível: uma espera legítima pintada de vermelho,
// que ensina o operador a ignorar a tela vermelha.
const PAIR_WAIT_CODE_PATTERN = /PAIR|SYNC|WAIT/;
const PAIR_WAIT_MESSAGE_PATTERN = /aguard\w*[^.]{0,20}\bpar\b/i;
const PAIR_WAIT_DETAIL_KEYS = Object.freeze([
    'pair_service_order_code',
    'pair_service_order_id',
    'pair_item_seq',
    'pair_item_status',
    'waiting_for_pair',
]);

/**
 * ESPERA do par (§6.5) — `null` quando o 409 não é isso.
 *
 * Devolve também O QUE FALTA, que é a diferença entre uma tela útil ("a outra
 * metade ainda não entregou o item 3 no STG-R02-00-00") e uma inútil ("tente
 * mais tarde").
 */
export function ParsePairWait(error) {
    if (!isConflict(error)) {
        return null;
    }
    const details = errorDetails(error);
    const code = errorCode(error);
    const matchesCode = PAIR_WAIT_CODE_PATTERN.test(code);
    const matchesDetails = PAIR_WAIT_DETAIL_KEYS.some((key) => details[key] !== undefined && details[key] !== null);
    const matchesMessage = PAIR_WAIT_MESSAGE_PATTERN.test(String(error?.message || ''));

    if (!matchesCode && !matchesDetails && !matchesMessage) {
        return null;
    }

    return {
        message: error?.message || null,
        pairCode: details.pair_service_order_code ?? details.pair_code ?? null,
        pairSeq: details.pair_item_seq ?? details.pair_seq ?? null,
        pairStatus: details.pair_item_status ?? details.pair_status ?? null,
        locationCode: details.pair_location_code ?? details.location_code ?? null,
    };
}

/**
 * A pausa foi recusada por FALTAR o endereço da carga.
 *
 * Acontece quando o sistema sabe de um pallet na mão que a tela ainda não
 * sabia (item já `EXECUTING` no backend, coletor reaberto no meio do item). Não
 * é erro do operador: é o sistema pedindo o bipe que o §6.5 exige — então a
 * tela troca para o passo do bipe em vez de mostrar uma mensagem de falha.
 */
export function IsDropLocationRequiredError(error) {
    if (Number(error?.status) !== 400) {
        return false;
    }
    return /drop_location_code/.test(String(error?.message || ''));
}

// ── As duas ações que só existem no fluxo de OS ─────────────────────────────
// Passam pelos métodos NOMEADOS de `api.js` (o transporte do projeto: token,
// refresh, sonda de backend fora do ar). Ficam embrulhadas aqui porque o
// coletor fala em `carryingLpnCode`/`dropLocationCode` e a decisão de QUANDO
// mandar `scan_method` é de fluxo, não de transporte.

/** Pausa com motivo — e, carregando algo, o endereço onde a carga ficou. */
export function PauseServiceOrder(
    id,
    { reason, operatorCode = null, carryingLpnCode = null, dropLocationCode = null, scanMethod = null } = {},
) {
    return api.pauseWarehouseServiceOrder(id, {
        reason,
        operatorCode,
        carryingLpnCode,
        dropLocationCode,
        // Só descreve a leitura do endereço de descarga: sem carga não há bipe,
        // e mandar o campo assim mesmo seria protocolo inventado.
        scanMethod: dropLocationCode ? scanMethod : null,
    });
}

/**
 * Item que NÃO deu certo: marca `SKIPPED_ALERT` e abre o incidente já vinculado
 * ao item. Endpoint SEPARADO do execute de propósito (decisão registrada no
 * contrato do M5) — "executar com flag de não executei" é a assinatura que faz
 * alguém pular item sem querer.
 */
export function SkipServiceOrderItem(id, itemId, { operatorCode = null, kind, notes = null } = {}) {
    return api.skipWarehouseServiceOrderItem(id, itemId, {
        operator_code: operatorCode || undefined,
        kind,
        notes: notes || undefined,
    });
}
